'use client'

import React, { FC, ReactNode, useEffect, useState } from 'react'
import { Wrapper } from '@googlemaps/react-wrapper'
import * as Sentry from '@sentry/nextjs'
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk'
import SessionReplay from '@launchdarkly/session-replay'
import { I18nextProvider } from 'react-i18next'

import { ToastContextProvider } from '~/src/components/generic/Toast/context'

import { NotificationContextProvider } from '~/src/components/shared/Notification/context'

import { MixpanelEvent, startMixpanelClient, track } from '~/clients/mixpanelClient'
import withApollo from '~/hoc/withApollo'
import i18n from '~/lib/i18n'

import '~/src/styles/globals.css'

const LD_INIT_TIMEOUT_SECONDS = 0.3

const MyApp: FC<{ children: ReactNode }> = ({ children }) => {
  useEffect(() => {
    startMixpanelClient()
    track(MixpanelEvent.AppLoad)
  }, [])

  return (
    <Sentry.ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <ToastContextProvider>
          <NotificationContextProvider>
            <Wrapper
              apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ?? ''}
              language={i18n.language}
              libraries={['marker']}
            >
              {children}
            </Wrapper>
          </NotificationContextProvider>
        </ToastContextProvider>
      </I18nextProvider>
    </Sentry.ErrorBoundary>
  )
}

const LDApp: FC<{ children: ReactNode }> = ({ children }) => {
  const [LDProvider, setLDProvider] = useState<React.ComponentType<{
    children: ReactNode
  }> | null>(null)
  const [ldReady, setLdReady] = useState(false)

  useEffect(() => {
    asyncWithLDProvider({
      clientSideID: process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_SIDE_ID ?? '',
      context: {
        kind: 'user',
        anonymous: true,
      },
      timeout: LD_INIT_TIMEOUT_SECONDS,
      options: {
        bootstrap: 'localStorage',
        plugins: [
          new SessionReplay({
            privacySetting: 'none',
          }),
        ],
      },
    })
      .then((Provider) => setLDProvider(() => Provider))
      .finally(() => setLdReady(true))
  }, [])

  if (!ldReady) return null

  const app = <MyApp>{children}</MyApp>

  return LDProvider ? <LDProvider>{app}</LDProvider> : app
}

export default withApollo()(LDApp)
